import { useEffect, useState } from "react";
import { UseMutationResult } from "@tanstack/react-query";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@material-ui/core/Button";
import AddCircle from "@material-ui/icons/AddCircle";
import Dialog from "@material-ui/core/Dialog";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import Typography from "@material-ui/core/Typography";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from "@material-ui/core/FormControlLabel";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import Grid from "@material-ui/core/Grid";
import UsersField from "./components/users-field";
import { schema } from "./schema";
import { useFormStyles } from "./styles";
import { useAttendanceLocalStyles } from "../../../../../../../../../styles";
import { AttendanceLocalDepartmentGuicheCreateData } from "../../../../../../../../../../../services/api-tansquery/attendance-local/department/guiche/create";
import { AttendanceLocalDepartmentGuicheUpdateData } from "../../../../../../../../../../../services/api-tansquery/attendance-local/department/guiche/update";
import { AttendanceLocalDepartmentGuiche } from "../../../../../../../../../../../@types/attendance-local-department-guiche";
import { AttendanceLocal } from "../../../../../../../../../../../@types/attendance-local";
import { AttendanceLocalDepartment } from "../../../../../../../../../../../@types/attendance-local-department";
import { attendanceTypes } from "../../../../../../../../../../../store/attendance-types";

type FormProps = {
    departmentId: AttendanceLocalDepartment["id"];
    slug: AttendanceLocal["slug_unidade"];
    guiche?: AttendanceLocalDepartmentGuiche;
    buttonTrigger?: React.ReactNode;
    mutation: UseMutationResult<
        AttendanceLocalDepartmentGuiche,
        unknown,
        | AttendanceLocalDepartmentGuicheCreateData
        | AttendanceLocalDepartmentGuicheUpdateData,
        unknown
    >
};

export default function Form({ departmentId, slug, guiche, buttonTrigger, mutation }: FormProps) {
    const [open, setOpen] = useState(false);
    const { classes } = useFormStyles();
    const { classes: defaultClasses } = useAttendanceLocalStyles();
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            ordem: "",
            tipo: "",
            setor: departmentId.toString(),
            ativo: true,
            user: [],
        }
    });

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            setor: Number(data.setor),
            ordem: Number(data.ordem),
            tipo: data.tipo,
            ativo: data.ativo,
            user: data.user,
        });
    }

    useEffect(() => {
        if (open && guiche) {
            form.reset({
                setor: guiche.setor.toString(),
                ordem: guiche.ordem.toString(),
                tipo: guiche.tipo,
                ativo: guiche.ativo,
                user: guiche.users.map(user => user.username) || [],
            });
        } else {
            form.reset();
        }
    }, [open, guiche, form]);

    useEffect(() => {
        if (mutation.isSuccess) {
            setOpen(false);
        }
    }, [mutation.isSuccess]);

    return (
        <>
            {buttonTrigger ? (
                <span onClick={() => setOpen(true)}>
                    {buttonTrigger}
                </span>
            ) : (
                <Button
                    startIcon={<AddCircle />}
                    className={defaultClasses.addButton}
                    onClick={() => setOpen(true)}
                >
                    Guichê de Atendimento
                </Button>
            )}
            <Dialog open={open}>
                <Card
                    component="form"
                    onSubmit={form.handleSubmit(handleSubmit)}
                >
                    <CardHeader
                        avatar={<AddCircle />}
                        className={classes.cardHeader}
                        title={<Typography variant="h5">Guichê de Atendimento</Typography>}
                    />
                    <CardContent
                        container
                        spacing={2}
                        component={Grid}
                    >
                        <Grid item xs={3}>
                            <Controller
                                name="ordem"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        required
                                        fullWidth
                                        label="Ordem"
                                        type="number"
                                        variant="outlined"
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        style={{ flex: 1 }}
                                        inputProps={{
                                            min: 1,
                                        }}
                                        InputProps={{
                                            required: false,
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={9}>
                            <Controller
                                name="tipo"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <FormControl
                                        variant="outlined"
                                        className={classes.formControl}
                                        error={!!fieldState.error}
                                    >
                                        <InputLabel id="attendance-types-label" required>
                                            Tipo de atendimento
                                        </InputLabel>
                                        <Select
                                            id="attendance-types"
                                            labelId="attendance-types-label"
                                            value={field.value}
                                            label="Tipo de atendimento"
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                            }}
                                            MenuProps={{
                                                anchorOrigin: {
                                                    vertical: "bottom",
                                                    horizontal: "left",
                                                },
                                                transformOrigin: {
                                                    vertical: "top",
                                                    horizontal: "left",
                                                },
                                                getContentAnchorEl: null,
                                            }}
                                        >
                                            {attendanceTypes.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            {fieldState.error && fieldState.error.message}
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="user"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <UsersField
                                        slug={slug}
                                        field={field}
                                        fieldState={fieldState}
                                        users={guiche?.users}
                                        style={{ flex: 3 }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} style={{ paddingLeft: 10 }}>
                            <Controller
                                name="ativo"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <FormControl error={!!fieldState.error}>
                                        <FormControlLabel
                                            label="Este guichê está ativo?"
                                            control={<Switch {...field} checked={field.value} />}
                                        />
                                        <FormHelperText>
                                            {fieldState.error?.message || "Ative esta opção para permitir que este guichê seja exibido como disponível."}
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                    </CardContent>
                    <CardActions className={classes.cardActions}>
                        <Button
                            className={classes.button}
                            onClick={() => setOpen(false)}
                            disabled={mutation.isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className={defaultClasses.addButton}
                            disabled={mutation.isLoading}
                        >
                            {mutation.isLoading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </CardActions>
                </Card>
            </Dialog >
        </>
    );
}
